import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { isApiError } from "@bw/shared/api";
import type { AddressResult } from "@bw/shared/components";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useMyOrg } from "@/hooks/use-my-org";
import { useSelectedFacility } from "@/app/FacilityContext";
import { getTimezone } from "@/libs/geoApi";
import {
  createFacility,
  updateFacility,
  createCourt,
  getOperatingHours,
  setOperatingHours,
  type CourtCreate,
  type FacilityCreateInput,
  type FacilityUpdateInput,
} from "@/libs/facilityApi";
import {
  StructuredAddressSection,
  addressDraftToAddress,
  composeAddressLocation,
  createAddressDraft,
  hasSameAddressDefinition,
  isAddressDraftVerified,
  type AddressDraft,
} from "@/components/address/StructuredAddressSection";
import {
  FacilityApprovalPanel,
  type FacilityApprovalSubject,
} from "./facility-approval";
import {
  OPERATING_HOURS_DAYS,
  emptyOperatingHoursDraft,
  operatingHoursDraftToCommand,
  validateOperatingHoursDraft,
  type OperatingHoursDraft,
} from "./operating-hours";
import { OperatingHoursEditor } from "./operating-hours-editor";

/**
 * CreateFacility (route /facilities/new) — the multi-step create-facility wizard
 * (F7). Replaces the thin single-step name+location+type form.
 *
 * Steps: 1 details → 2 courts → 3 hours → 4 review → single "Create facility".
 *
 * Collect-then-create-at-end: ALL data is held in client state across the four
 * steps; NOTHING is POSTed until the final Create. On Create the sequence is
 * createFacility FIRST → take the RETURNED facility_id → createCourt(returnedId,
 * each) per court → setOperatingHours(returnedId, policy). A partial failure
 * (facility made, but courts/hours fail) is surfaced TRUTHFULLY — never as a
 * fake "all set" success. Full setup completion stays in this flow and requires
 * a separate, explicit approval submission.
 *
 * Facility remains authoritative for validation. The wizard blocks malformed or
 * overlapping hours early, forwards one Facility-owned effective policy, and surfaces server
 * errors verbatim.
 */

// ---------------------------------------------------------------------------
// Constants — facility types + the enabled-services allowlist (matches the
// onboarding facility panel + backend ALL_SERVICES). day_of_week: 0=Sun … 6=Sat.
// ---------------------------------------------------------------------------

const DEFAULT_TIMEZONE = "America/Los_Angeles";

// Only statuses that prove the command was rejected before a successful
// response leave a failed row editable. Timeouts, uncommon proxy statuses, 5xx,
// network failures, and malformed success responses remain uncertain and must
// be resolved by replaying the same resource key.
const CONCLUSIVE_COURT_CREATE_FAILURE_STATUSES: ReadonlySet<number> = new Set([
  400, 401, 403, 404, 405, 409, 410, 412, 413, 415, 422, 423, 429,
]);

function isCourtCreateOutcomeUncertain(error: unknown): boolean {
  return (
    !isApiError(error) ||
    !CONCLUSIVE_COURT_CREATE_FAILURE_STATUSES.has(error.status)
  );
}

type FacilityType = "venue" | "coach_partner" | "merchant";

const FACILITY_TYPES: ReadonlyArray<{ value: FacilityType; label: string }> = [
  { value: "venue", label: "Venue" },
  { value: "coach_partner", label: "Coach partner" },
  { value: "merchant", label: "Merchant" },
];

const ENABLED_SERVICES = [
  { value: "court_reservation", label: "Court reservations" },
  { value: "open_play", label: "Drop-in" },
  { value: "classes", label: "Classes" },
  { value: "coaching", label: "Coaching" },
  { value: "memberships", label: "Memberships" },
] as const;

type ServiceValue = (typeof ENABLED_SERVICES)[number]["value"];

const STEPS = ["Details", "Courts", "Operating hours", "Review"] as const;

function facilityLocalDate(timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const value = (kind: string) => parts.find((part) => part.type === kind)?.value;
  const year = value("year");
  const month = value("month");
  const day = value("day");
  if (!year || !month || !day) {
    throw new Error("Could not resolve the facility-local operating date.");
  }
  return `${year}-${month}-${day}`;
}

/**
 * A completely closed initial draft means the owner intentionally deferred
 * policy setup. It is valid only in this creation flow: the facility remains a
 * draft and the normal operating-hours workspace owns the follow-up change.
 */
function isEmptyInitialOperatingHoursDraft(draft: OperatingHoursDraft): boolean {
  return (
    draft.schedules.length === 1 &&
    draft.dateOverrides.length === 0 &&
    draft.closurePeriods.length === 0 &&
    OPERATING_HOURS_DAYS.every(
      ({ dayOfWeek }) => !draft.schedules[0].week[dayOfWeek].enabled,
    )
  );
}

function withInitialEffectiveDate(
  draft: OperatingHoursDraft,
  timezone: string,
): OperatingHoursDraft {
  const [firstSchedule, ...remainingSchedules] = draft.schedules;
  if (!firstSchedule || firstSchedule.effectiveFrom) return draft;
  return {
    ...draft,
    schedules: [
      {
        ...firstSchedule,
        effectiveFrom: facilityLocalDate(timezone),
      },
      ...remainingSchedules,
    ],
  };
}

// ---------------------------------------------------------------------------
// Client-held wizard state shapes. A court row carries a name + a bookable/drop-in
// tag toggle; an hours row carries the open/close window for one day.
// ---------------------------------------------------------------------------

interface CourtRow {
  rowId: number;
  idempotencyKey: string;
  name: string;
  bookable: boolean;
}

/** A single inline error/partial-failure banner the operator can act on. */
type Outcome =
  | { kind: "facility-failed"; message: string }
  | { kind: "partial"; message: string };

type TimezoneResolutionStatus = "idle" | "pending" | "resolved";

function identityUpdatePayload(
  payload: FacilityCreateInput,
): FacilityUpdateInput {
  return {
    name: payload.name,
    location: payload.location,
    address: payload.address,
    facility_type: payload.facility_type,
    timezone: payload.timezone,
    enabled_services: payload.enabled_services ?? [],
    staff_check_in_available: payload.staff_check_in_available ?? true,
  };
}

export function CreateFacility() {
  const navigate = useNavigate();
  const { setFacility } = useSelectedFacility();
  const orgView = useMyOrg();
  const orgId = orgView.org?.org_id ?? null;
  const canCreateFacility = Boolean(orgView.org?.owner_user_id);

  const [step, setStep] = useState(0);

  // Step 1 — details.
  const [name, setName] = useState("");
  const [addressDraft, setAddressDraft] = useState(() => createAddressDraft());
  const addressDraftRef = useRef(addressDraft);
  const addressVersionRef = useRef(0);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [timezoneResolutionStatus, setTimezoneResolutionStatus] =
    useState<TimezoneResolutionStatus>("idle");
  const [facilityType, setFacilityType] = useState<FacilityType>("venue");
  const [services, setServices] = useState<Set<ServiceValue>>(
    () => new Set<ServiceValue>(["court_reservation"]),
  );
  const [staffCheckIn, setStaffCheckIn] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);

  // Step 2 — courts (optional, zero allowed).
  const [courts, setCourts] = useState<CourtRow[]>([]);
  const [nextCourtId, setNextCourtId] = useState(1);

  // Step 3 uses the same full Facility-owned policy editor as Settings. A
  // facility may deliberately remain closed/unconfigured while still a draft.
  const [hours, setHours] = useState<OperatingHoursDraft>(() =>
    emptyOperatingHoursDraft(),
  );
  const [hoursError, setHoursError] = useState<string | null>(null);

  // Create-at-end status.
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [approvalFacility, setApprovalFacility] =
    useState<FacilityApprovalSubject | null>(null);
  const [setupComplete, setSetupComplete] = useState(false);

  // Per-step persistence across retries (data integrity). createFacility must run
  // AT MOST ONCE per facility: once it succeeds, the returned id is stored here so
  // a retry after a partial failure SKIPS the create and only re-runs the steps
  // that have NOT yet succeeded. `createdCourtRowIds` tracks which court rows were
  // already created (so a retry re-creates only the ones that failed); `hoursDone`
  // tracks whether the operating-hours write already succeeded.
  const createdFacilityIdRef = useRef<string | null>(null);
  const creationIdempotencyKeyRef = useRef(globalThis.crypto.randomUUID());
  const createdFacilityNameRef = useRef<string>("");
  const createdFacilityTypeRef = useRef<string | null>(null);
  const createdFacilityIdentityFingerprintRef = useRef<string | null>(null);
  const createdCourtRowIdsRef = useRef<Set<number>>(new Set());
  const uncertainCourtRowIdsRef = useRef<Set<number>>(new Set());
  const hoursDoneRef = useRef(false);

  const nameValid = name.trim().length > 0;
  const addressValid = isAddressDraftVerified(addressDraft);

  function toggleService(value: ServiceValue) {
    setServices((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  function addCourt() {
    setCourts((current) => [
      ...current,
      {
        rowId: nextCourtId,
        idempotencyKey: globalThis.crypto.randomUUID(),
        name: "",
        bookable: true,
      },
    ]);
    setNextCourtId((id) => id + 1);
  }

  function removeCourt(rowId: number) {
    if (
      createdCourtRowIdsRef.current.has(rowId) ||
      uncertainCourtRowIdsRef.current.has(rowId)
    ) {
      return;
    }
    setCourts((current) => current.filter((c) => c.rowId !== rowId));
  }

  function updateCourtName(rowId: number, value: string) {
    if (
      createdCourtRowIdsRef.current.has(rowId) ||
      uncertainCourtRowIdsRef.current.has(rowId)
    ) {
      return;
    }
    setCourts((current) =>
      current.map((c) => (c.rowId === rowId ? { ...c, name: value } : c)),
    );
  }

  function toggleCourtBookable(rowId: number) {
    if (
      createdCourtRowIdsRef.current.has(rowId) ||
      uncertainCourtRowIdsRef.current.has(rowId)
    ) {
      return;
    }
    setCourts((current) =>
      current.map((c) =>
        c.rowId === rowId ? { ...c, bookable: !c.bookable } : c,
      ),
    );
  }

  function validateInitialOperatingHours(): string | null {
    if (isEmptyInitialOperatingHoursDraft(hours)) return null;
    return validateOperatingHoursDraft(hours);
  }

  function goNext() {
    if (step === 0) {
      if (!nameValid) {
        setNameError("Enter a facility name to continue.");
        return;
      }
      if (!addressValid) {
        setAddressError("Verify the facility address to continue.");
        return;
      }
      if (timezoneResolutionStatus !== "resolved" || !timezone) {
        setAddressError(
          timezoneResolutionStatus === "pending"
            ? "Wait for the address timezone to finish resolving."
            : "Verify the facility address to continue.",
        );
        return;
      }
      setHours((current) => withInitialEffectiveDate(current, timezone));
    }
    if (step === 2) {
      const validationError = validateInitialOperatingHours();
      if (validationError) {
        setHoursError(validationError);
        return;
      }
      setHoursError(null);
    }
    setNameError(null);
    setAddressError(null);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function goBack() {
    setStep((s) => Math.max(s - 1, 0));
  }

  function updateAddressDraft(next: AddressDraft) {
    const addressDefinitionChanged = !hasSameAddressDefinition(
      addressDraftRef.current,
      next,
    );
    addressDraftRef.current = next;
    setAddressDraft(next);
    if (addressDefinitionChanged) {
      addressVersionRef.current += 1;
      setTimezone(null);
      setTimezoneResolutionStatus("idle");
    }
    setAddressError(null);
  }

  async function handleAddressVerified(result: AddressResult) {
    const resolutionVersion = addressVersionRef.current + 1;
    addressVersionRef.current = resolutionVersion;
    setTimezone(null);
    setTimezoneResolutionStatus("pending");

    let resolvedTimezone: string;
    try {
      const resolved = await getTimezone(result.lat, result.lng);
      resolvedTimezone = resolved.timezone;
    } catch {
      resolvedTimezone = DEFAULT_TIMEZONE;
    }

    const currentDraft = addressDraftRef.current;
    if (
      addressVersionRef.current !== resolutionVersion ||
      !isAddressDraftVerified(currentDraft) ||
      currentDraft.latitude !== result.lat ||
      currentDraft.longitude !== result.lng
    ) {
      return;
    }

    setTimezone(resolvedTimezone);
    setTimezoneResolutionStatus("resolved");
  }

  // Build the typed payload from the collected client state. Drop-in
  // availability + price are configured via an open_play rate in the Rates
  // tab AFTER the facility exists (Slice 1, unified pricing) -- there is no
  // drop-in price field to collect here.
  const facilityPayload = useMemo((): FacilityCreateInput | null => {
    if (!orgId || !timezone || timezoneResolutionStatus !== "resolved") {
      return null;
    }
    const address = addressDraftToAddress(addressDraft);
    if (!address) return null;
    const enabled = ENABLED_SERVICES.map((s) => s.value).filter((v) =>
      services.has(v),
    );
    const body: FacilityCreateInput & {
      enabled_services?: string[];
      staff_check_in_available?: boolean;
    } = {
      idempotency_key: creationIdempotencyKeyRef.current,
      organization_id: orgId,
      name: name.trim(),
      location: composeAddressLocation(addressDraft),
      address,
      facility_type: facilityType,
      timezone,
      enabled_services: enabled,
      staff_check_in_available: staffCheckIn,
    };
    return body;
  }, [
    orgId,
    name,
    addressDraft,
    timezone,
    timezoneResolutionStatus,
    facilityType,
    services,
    staffCheckIn,
  ]);

  // Court payloads paired with their stable row id, so a retry can re-create only
  // the rows that have NOT yet succeeded (tracked in createdCourtRowIdsRef).
  function courtPayloads(): Array<{ rowId: number; body: CourtCreate }> {
    return courts
      .filter((c) => c.name.trim().length > 0)
      .map((c) => ({
        rowId: c.rowId,
        body: {
          idempotency_key: c.idempotencyKey,
          name: c.name.trim(),
          status: "enabled",
          tags: c.bookable ? ["bookable"] : ["drop_in"],
        },
      }));
  }

  // Collect-then-create-at-end, idempotent on retry. createFacility runs AT MOST
  // ONCE per facility: its returned id is stored in createdFacilityIdRef, so a
  // retry after a partial failure SKIPS the create and re-runs ONLY the steps that
  // have not yet succeeded (the failed courts + hours). This makes a retry COMPLETE
  // the under-configured facility instead of minting a duplicate. The in-flight
  // `submitting` guard additionally collapses a double-click into one run.
  async function handleCreate() {
    if (submitting) return;
    const hoursValidationError = validateInitialOperatingHours();
    if (hoursValidationError) {
      setHoursError(hoursValidationError);
      setStep(2);
      return;
    }
    if (!facilityPayload) {
      setOutcome({
        kind: "facility-failed",
        message: "Your organization is still loading. Try again in a moment.",
      });
      return;
    }
    setSubmitting(true);
    setOutcome(null);

    const latestIdentityPayload = identityUpdatePayload(facilityPayload);
    const latestIdentityFingerprint = JSON.stringify(latestIdentityPayload);

    // Step 1 — create the facility only once. If an operator edits its identity
    // after a downstream partial failure, persist those edits before retrying
    // courts and hours.
    if (createdFacilityIdRef.current === null) {
      try {
        const created = await createFacility(facilityPayload);
        createdFacilityIdRef.current = created.facility_id;
        createdFacilityNameRef.current = created.name;
        createdFacilityTypeRef.current = created.facility_type ?? null;
        createdFacilityIdentityFingerprintRef.current =
          latestIdentityFingerprint;
        setApprovalFacility({
          facility_id: created.facility_id,
          name: created.name,
          approval_status: created.approval_status,
          approval_rejection_reason: created.approval_rejection_reason,
        });
      } catch (err) {
        // Facility-create failure: NOTHING downstream is posted, no navigation.
        setOutcome({
          kind: "facility-failed",
          message:
            err instanceof Error ? err.message : "Failed to create facility.",
        });
        setSubmitting(false);
        return;
      }
    } else if (
      createdFacilityIdentityFingerprintRef.current !==
      latestIdentityFingerprint
    ) {
      try {
        const updated = await updateFacility(
          createdFacilityIdRef.current,
          latestIdentityPayload,
        );
        createdFacilityNameRef.current = updated.name;
        createdFacilityTypeRef.current = updated.facility_type ?? null;
        createdFacilityIdentityFingerprintRef.current =
          latestIdentityFingerprint;
        setApprovalFacility({
          facility_id: updated.facility_id,
          name: updated.name,
          approval_status: updated.approval_status,
          approval_rejection_reason: updated.approval_rejection_reason,
        });
      } catch (err) {
        setOutcome({
          kind: "partial",
          message:
            err instanceof Error
              ? `Facility created, but saving updated details failed: ${err.message}`
              : "Facility created, but saving updated details failed. Try again.",
        });
        setSubmitting(false);
        return;
      }
    }

    const facilityId = createdFacilityIdRef.current;
    if (!facilityId) {
      setOutcome({
        kind: "facility-failed",
        message: "The facility response did not include an identifier.",
      });
      setSubmitting(false);
      return;
    }

    // Facility exists. The new facility becomes the active selection regardless —
    // it is real now, even if a downstream step still fails.
    setFacility(
      facilityId,
      createdFacilityNameRef.current,
      createdFacilityTypeRef.current,
    );

    // Step 2 — create only the courts that have NOT already succeeded (a retry
    // re-creates just the previously-failed rows; already-created rows are skipped).
    let courtsFailed = false;
    for (const { rowId, body } of courtPayloads()) {
      if (createdCourtRowIdsRef.current.has(rowId)) continue;
      try {
        await createCourt(facilityId, body);
        uncertainCourtRowIdsRef.current.delete(rowId);
        createdCourtRowIdsRef.current.add(rowId);
      } catch (err) {
        courtsFailed = true;
        // A network/parse failure or upstream 5xx cannot prove whether the
        // resource committed. Freeze that row until an exact keyed replay
        // resolves it; ordinary 4xx validation failures remain editable.
        if (isCourtCreateOutcomeUncertain(err)) {
          uncertainCourtRowIdsRef.current.add(rowId);
        }
      }
    }

    // Step 3 — persist the initial Facility-owned operating policy only if not
    // already done. Courts may have advanced the reservation revision, so read
    // the authoritative snapshot immediately before the revision-bound write.
    let hoursFailed = false;
    if (!hoursDoneRef.current) {
      try {
        if (!isEmptyInitialOperatingHoursDraft(hours)) {
          const snapshot = await getOperatingHours(facilityId);
          await setOperatingHours(facilityId, {
            ...operatingHoursDraftToCommand(hours),
            expected_reservation_state_revision:
              snapshot.reservation_state_revision,
          });
        }
        hoursDoneRef.current = true;
      } catch {
        hoursFailed = true;
      }
    }

    if (courtsFailed || hoursFailed) {
      // Honest, PRECISE partial-failure — name only what actually failed, never a
      // fake "all set"/"success". The facility-made fact is stated first.
      const failedParts: string[] = [];
      if (courtsFailed) failedParts.push("adding courts");
      if (hoursFailed) failedParts.push("saving hours");
      setOutcome({
        kind: "partial",
        message: `Facility created, but ${failedParts.join(
          " and ",
        )} failed. Click Create facility to retry the remaining steps, or go to the dashboard to finish setup there.`,
      });
      setSubmitting(false);
      return;
    }

    // Setup succeeded, but the facility is still non-operational. Keep the owner
    // in the creation journey and require the separate approval submission.
    setSubmitting(false);
    setSetupComplete(true);
  }

  if (!canCreateFacility) {
    return (
      <Alert severity="info" sx={{ mx: { xs: 2, lg: 3 }, my: 4 }}>
        Only the organization owner can create a facility.
      </Alert>
    );
  }

  if (setupComplete && approvalFacility) {
    return (
      <Stack
        spacing={2}
        sx={{ mx: "auto", px: { xs: 2, lg: 3 }, width: "100%", maxWidth: 760 }}
      >
        <FacilityApprovalPanel
          facility={approvalFacility}
          onFacilityChange={(updated) => {
            setApprovalFacility({
              facility_id: updated.facility_id,
              name: updated.name,
              approval_status: updated.approval_status,
              approval_rejection_reason: updated.approval_rejection_reason,
            });
          }}
        />
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Button
            type="button"
            size="small"
            variant="outlined"
            onClick={() => navigate("/org")}
          >
            Back to facilities
          </Button>
          <Button
            type="button"
            size="small"
            variant="outlined"
            onClick={() => navigate("/settings/facility")}
          >
            Facility settings
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Box
      sx={{ mx: "auto", px: { xs: 2, lg: 3 }, width: "100%", maxWidth: 760 }}
    >
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Stack spacing={0.75} sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
          <Typography component="h1" variant="h5" sx={{ fontWeight: 800 }}>
            Create facility
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Add a new facility to your organization. Nothing is saved until you
            review and create.
          </Typography>
        </Stack>

        <Box sx={{ px: { xs: 1, sm: 3 }, py: 3 }}>
          <Stepper activeStep={step} alternativeLabel aria-label="Wizard steps">
            {STEPS.map((label, index) => (
              <Step key={label} completed={index < step}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Divider />

        <Stack spacing={3} sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
          <Typography
            color="text.secondary"
            variant="overline"
            sx={{ fontWeight: 800, lineHeight: 1.5 }}
          >
            Step {step + 1} of {STEPS.length} — {STEPS[step]}
          </Typography>

          {step === 0 ? (
            <DetailsStep
              name={name}
              setName={setName}
              nameError={nameError}
              addressDraft={addressDraft}
              setAddressDraft={updateAddressDraft}
              addressError={addressError}
              onAddressVerified={handleAddressVerified}
              facilityType={facilityType}
              setFacilityType={setFacilityType}
              services={services}
              toggleService={toggleService}
              staffCheckIn={staffCheckIn}
              setStaffCheckIn={setStaffCheckIn}
            />
          ) : null}

          {step === 1 ? (
            <CourtsStep
              courts={courts}
              persistedCourtRowIds={createdCourtRowIdsRef.current}
              uncertainCourtRowIds={uncertainCourtRowIdsRef.current}
              addCourt={addCourt}
              removeCourt={removeCourt}
              updateCourtName={updateCourtName}
              toggleCourtBookable={toggleCourtBookable}
            />
          ) : null}

          {step === 2 ? (
            <Stack spacing={2}>
              <OperatingHoursEditor
              value={hours}
              onChange={(next) => {
                setHours(next);
                setHoursError(null);
                // A partial create may leave the facility and its first hours
                // write persisted while a court failed. Editing after going
                // Back makes the hours step pending again, so the retry applies
                // the new draft without creating another facility.
                if (createdFacilityIdRef.current !== null) {
                  hoursDoneRef.current = false;
                }
              }}
              />
              {hoursError ? <Alert severity="error">{hoursError}</Alert> : null}
            </Stack>
          ) : null}

          {step === 3 ? (
            <ReviewStep
              name={name.trim()}
              location={composeAddressLocation(addressDraft)}
              facilityType={facilityType}
              services={[...services]}
              staffCheckIn={staffCheckIn}
              courts={courts.filter((c) => c.name.trim().length > 0)}
              hours={hours}
            />
          ) : null}

          {outcome ? (
            <Alert
              severity={
                outcome.kind === "facility-failed" ? "error" : "warning"
              }
            >
              <Stack spacing={1}>
                <Typography variant="body2">{outcome.message}</Typography>
                {outcome.kind === "partial" ? (
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    onClick={() => navigate("/dashboard")}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Go to the dashboard
                  </Button>
                ) : null}
              </Stack>
            </Alert>
          ) : null}

          {/* Footer — Back / Next on early steps; Create on Review. */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={step === 0 ? () => navigate("/org") : goBack}
              disabled={submitting}
            >
              {step === 0 ? "Cancel" : "Back"}
            </Button>

            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                size="small"
                variant="contained"
                onClick={goNext}
                disabled={
                  submitting ||
                  (step === 0 && timezoneResolutionStatus === "pending")
                }
              >
                Next
              </Button>
            ) : (
              <Button
                type="button"
                size="small"
                variant="contained"
                onClick={handleCreate}
                disabled={submitting}
                startIcon={
                  submitting ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : undefined
                }
              >
                {submitting ? "Creating…" : "Create facility"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Details.
// ---------------------------------------------------------------------------

function DetailsStep(props: {
  name: string;
  setName: (v: string) => void;
  nameError: string | null;
  addressDraft: AddressDraft;
  setAddressDraft: (v: AddressDraft) => void;
  addressError: string | null;
  onAddressVerified: (result: AddressResult) => Promise<void>;
  facilityType: FacilityType;
  setFacilityType: (v: FacilityType) => void;
  services: Set<ServiceValue>;
  toggleService: (v: ServiceValue) => void;
  staffCheckIn: boolean;
  setStaffCheckIn: (v: boolean) => void;
}) {
  return (
    <Stack spacing={3}>
      <TextField
        error={Boolean(props.nameError)}
        fullWidth
        helperText={props.nameError}
        id="facility-name"
        label="Name"
        onChange={(event) => props.setName(event.target.value)}
        placeholder="e.g. West Arena"
        size="small"
        value={props.name}
      />

      <StructuredAddressSection
        value={props.addressDraft}
        onChange={props.setAddressDraft}
        idPrefix="new-facility-address"
        autocompleteLabel="Facility Address"
        autocompletePlaceholder="Search the venue address"
        manualLine1Label="Facility Address Line 1"
        helperText="This is the public venue address players will use for discovery and directions."
        onAddressVerified={(result) => void props.onAddressVerified(result)}
        embedded
      />
      {props.addressError ? (
        <Alert severity="error">{props.addressError}</Alert>
      ) : null}

      <FormControl component="fieldset">
        <FormLabel component="legend" sx={{ mb: 1 }}>
          Facility type
        </FormLabel>
        <ToggleButtonGroup
          aria-label="Facility type"
          color="primary"
          exclusive
          fullWidth
          onChange={(_event, value: FacilityType | null) => {
            if (value) props.setFacilityType(value);
          }}
          size="small"
          value={props.facilityType}
        >
          {FACILITY_TYPES.map(({ value, label }) => (
            <ToggleButton
              key={value}
              value={value}
              sx={{ py: 1, textTransform: "none" }}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </FormControl>

      <FormControl component="fieldset">
        <FormLabel component="legend" sx={{ mb: 1 }}>
          Services offered
        </FormLabel>
        <FormGroup sx={{ gap: 1 }}>
          {ENABLED_SERVICES.map(({ value, label }) => (
            <Paper key={value} variant="outlined" sx={{ borderRadius: 2 }}>
              <FormControlLabel
                checked={props.services.has(value)}
                control={<Switch size="small" />}
                label={label}
                labelPlacement="start"
                onChange={() => props.toggleService(value)}
                sx={{
                  justifyContent: "space-between",
                  m: 0,
                  minHeight: 48,
                  px: 1.5,
                  width: "100%",
                  "& .MuiFormControlLabel-label": { fontWeight: 700 },
                }}
              />
            </Paper>
          ))}
        </FormGroup>
      </FormControl>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <FormControlLabel
          checked={props.staffCheckIn}
          control={<Switch size="small" />}
          label="Staff check-in available"
          labelPlacement="start"
          onChange={(_event, checked) => props.setStaffCheckIn(checked)}
          sx={{
            justifyContent: "space-between",
            m: 0,
            minHeight: 48,
            px: 1.5,
            width: "100%",
            "& .MuiFormControlLabel-label": { fontWeight: 700 },
          }}
        />
      </Paper>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Courts (optional, zero allowed).
// ---------------------------------------------------------------------------

function CourtsStep(props: {
  courts: CourtRow[];
  persistedCourtRowIds: ReadonlySet<number>;
  uncertainCourtRowIds: ReadonlySet<number>;
  addCourt: () => void;
  removeCourt: (rowId: number) => void;
  updateCourtName: (rowId: number, value: string) => void;
  toggleCourtBookable: (rowId: number) => void;
}) {
  return (
    <Stack spacing={2}>
      <Typography color="text.secondary" variant="body2">
        Add the courts players can book or drop in on. This is optional — you
        can add courts later from the facility.
      </Typography>

      {props.courts.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ borderStyle: "dashed", px: 2, py: 3, textAlign: "center" }}
        >
          <Typography color="text.secondary" variant="body2">
            No courts yet.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {props.courts.map((court, index) => {
            const persisted = props.persistedCourtRowIds.has(court.rowId);
            const uncertain = props.uncertainCourtRowIds.has(court.rowId);
            const locked = persisted || uncertain;
            return (
              <Paper
                data-court-row={court.rowId}
                key={court.rowId}
                variant="outlined"
                sx={{ p: 2 }}
              >
                <Box
                  sx={{
                    alignItems: { xs: "stretch", sm: "center" },
                    display: "grid",
                    gap: 1.5,
                    gridTemplateColumns: {
                      xs: "1fr auto",
                      sm: "minmax(12rem, 1fr) auto auto",
                    },
                  }}
                >
                  <TextField
                    fullWidth
                    id={`court-name-${court.rowId}`}
                    label="Court name"
                    onChange={(event) =>
                      props.updateCourtName(court.rowId, event.target.value)
                    }
                    placeholder={`Court ${index + 1}`}
                    size="small"
                    slotProps={{ htmlInput: { readOnly: locked } }}
                    sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
                    value={court.name}
                  />

                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: "center", minWidth: 100 }}
                  >
                    <Switch
                      checked={court.bookable}
                      disabled={locked}
                      onChange={() => props.toggleCourtBookable(court.rowId)}
                      size="small"
                      slotProps={{ input: { "aria-label": "Bookable" } }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {court.bookable ? "Bookable" : "Drop-in"}
                    </Typography>
                  </Stack>

                  <IconButton
                    aria-label="Remove court"
                    color="error"
                    disabled={locked}
                    onClick={() => props.removeCourt(court.rowId)}
                    size="small"
                    type="button"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                {locked ? (
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{ display: "block", mt: 1.5 }}
                  >
                    {uncertain
                      ? "Save outcome unknown. Retry setup before editing this court."
                      : "Saved during this setup. Finish setup before editing this court."}
                  </Typography>
                ) : null}
              </Paper>
            );
          })}
        </Stack>
      )}

      <Box>
        <Button
          type="button"
          size="small"
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={props.addCourt}
        >
          Add court
        </Button>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Review. Summarizes the entered data; NEVER renders a raw facility
// uuid (none exists yet — the facility is created on Create).
// ---------------------------------------------------------------------------

const TYPE_LABELS: Record<FacilityType, string> = {
  venue: "Venue",
  coach_partner: "Coach partner",
  merchant: "Merchant",
};

const SERVICE_LABELS: Record<string, string> = Object.fromEntries(
  ENABLED_SERVICES.map((s) => [s.value, s.label]),
);

function ReviewStep(props: {
  name: string;
  location: string;
  facilityType: FacilityType;
  services: ServiceValue[];
  staffCheckIn: boolean;
  courts: CourtRow[];
  hours: OperatingHoursDraft;
}) {
  const schedules = props.hours.schedules.map((schedule) => ({
    ...schedule,
    openDays: OPERATING_HOURS_DAYS.map((day) => ({
      ...day,
      hours: schedule.week[day.dayOfWeek],
    })).filter((day) => day.hours.enabled),
  }));

  return (
    <Stack divider={<Divider flexItem />} spacing={2.5}>
      <Box component="section">
        <Typography
          color="text.secondary"
          component="h3"
          variant="overline"
          sx={{ fontWeight: 800 }}
        >
          Facility
        </Typography>
        <Box component="dl" sx={{ display: "grid", gap: 1, m: 0, mt: 0.5 }}>
          <Row label="Name" value={props.name || "—"} />
          <Row label="Address" value={props.location || "Not set"} />
          <Row label="Type" value={TYPE_LABELS[props.facilityType]} />
          <Row
            label="Services"
            value={
              props.services.length
                ? props.services.map((s) => SERVICE_LABELS[s] ?? s).join(", ")
                : "None"
            }
          />
          <Row
            label="Staff check-in"
            value={props.staffCheckIn ? "Available" : "Not available"}
          />
        </Box>
      </Box>

      <Box component="section">
        <Typography
          color="text.secondary"
          component="h3"
          variant="overline"
          sx={{ fontWeight: 800 }}
        >
          Courts
        </Typography>
        {props.courts.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No courts added.
          </Typography>
        ) : (
          <Stack
            component="ul"
            spacing={1}
            sx={{ listStyle: "none", m: 0, mt: 0.5, p: 0 }}
          >
            {props.courts.map((court) => (
              <Stack
                component="li"
                direction="row"
                key={court.rowId}
                spacing={1}
                sx={{ alignItems: "center" }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {court.name}
                </Typography>
                <Chip
                  label={court.bookable ? "Bookable" : "Drop-in"}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      <Box component="section">
        <Typography
          color="text.secondary"
          component="h3"
          variant="overline"
          sx={{ fontWeight: 800 }}
        >
          Operating hours
        </Typography>
        {isEmptyInitialOperatingHoursDraft(props.hours) ? (
          <Typography color="text.secondary" variant="body2">
            Not configured yet. The facility will remain a draft until operating
            hours are configured.
          </Typography>
        ) : (
          <Stack
            component="ul"
            spacing={0.75}
            sx={{ listStyle: "none", m: 0, mt: 0.5, p: 0 }}
          >
            {schedules.map((schedule) => (
              <Stack component="li" key={schedule.id} spacing={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {schedule.effectiveFrom}
                  {schedule.effectiveUntil
                    ? ` through ${schedule.effectiveUntil}`
                    : " onward"}
                </Typography>
                {schedule.openDays.map((day) => (
                  <Typography
                    color="text.secondary"
                    component="span"
                    key={day.dayOfWeek}
                    variant="body2"
                  >
                    {day.label}: {day.hours.windows.map((window) => `${window.opensAt}–${window.closesAt}`).join(", ")}
                  </Typography>
                ))}
              </Stack>
            ))}
            {props.hours.dateOverrides.length > 0 ? (
              <Typography component="li" variant="body2">
                {props.hours.dateOverrides.length} date exception
                {props.hours.dateOverrides.length === 1 ? "" : "s"}
              </Typography>
            ) : null}
            {props.hours.closurePeriods.length > 0 ? (
              <Typography component="li" variant="body2">
                {props.hours.closurePeriods.length} closure period
                {props.hours.closurePeriods.length === 1 ? "" : "s"}
              </Typography>
            ) : null}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: "minmax(7rem, auto) minmax(0, 1fr)",
      }}
    >
      <Typography color="text.secondary" component="dt" variant="body2">
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{ fontWeight: 700, m: 0, textAlign: "right" }}
      >
        {value}
      </Typography>
    </Box>
  );
}
